import { FLAGS } from '@gozoki/feature-flags'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { type JSX, useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import {
  resolveDashboardCardState,
  retryFailedDashboardQueries,
} from '~/components/dashboards/dashboard-card-state'
import { Button } from '~/components/ui/button'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { BoutiqueChartModal, type BoutiqueScope } from './-boutique-chart-modal'
import { defaultForecastRange } from '../previsions-produits/-search-schema'
import { PREVISIONS_QUERY_OPTIONS } from './-previsions-query'
import { BoutiquesCard } from './-boutiques-card'
import { CategoriesTable } from './-categories-table'
import { FiltersBar } from './-filters-bar'
import {
  buildCategoryTree,
  buildStoreRows,
  buildTotals,
  type ForecastCategory,
} from './-previsions-data'
import { PrevisionsCardBoundary, PrevisionsCardSkeleton } from './-previsions-skeleton'
import { ProductionChart } from './-production-chart'
import { previsionsSearchSchema, SEGMENT_GROUP_BY, type Segment } from './-search-schema'
import { TotauxCard } from './-totaux-card'

export const Route = createFileRoute('/_authenticated/previsions/')({
  validateSearch: previsionsSearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  component: PrevisionsProductionPage,
})

withRoutePermissions(Route)

const PAGE_CLASS = 'flex h-[calc(100vh-1rem)] min-h-0 flex-col'
const HEADER_CLASS = 'shrink-0'
const TITLE_CLASS = 'font-brand text-[32px] font-extrabold leading-[36px] text-[#072c1a]'
const CONTAINER_CLASS = 'min-h-0 gap-2 rounded-none border-0 bg-transparent p-0'
const GRID_COLUMNS = '376px minmax(0, 1fr)'

function PrevisionsProductionPage() {
  const enabled = useFeatureFlag(FLAGS.forecastingXiuDashboard)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [dailyScope, setDailyScope] = useState<BoutiqueScope | null>(null)

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const fallbackRange = defaultForecastRange()
  const range = {
    start: search.startDate ?? fallbackRange.start,
    end: search.endDate ?? fallbackRange.end,
  }

  const storesQuery = useQuery(api.forecast.forecastStores.queryOptions({}))
  const stores = storesQuery.data ?? []
  const categoriesQuery = useQuery(api.forecast.productCategories.queryOptions({}, { enabled }))
  const categories = categoriesQuery.data ?? []
  const segmentCategoriesQuery = useQuery(
    api.forecast.segmentCategories.queryOptions({}, { enabled }),
  )

  // Le referentiel de legende suit l'axe choisi : commercial, interne ou reappro.
  const legendCategoriesFor = (segment: Segment): ForecastCategory[] => {
    if (segment === 'industriel') return segmentCategoriesQuery.data?.internal ?? []
    if (segment === 'reappro') return segmentCategoriesQuery.data?.reappro ?? []
    return categories
  }

  const filtersByCategory = search.categoryIds.length > 0
  const selectedCategories = categories.filter((category) =>
    search.categoryIds.includes(category.id),
  )
  const productIds = selectedCategories.flatMap((category) =>
    category.products.map((product) => product.id),
  )
  const appliedCategoryIds =
    productIds.length > 0 ? selectedCategories.map((category) => category.id) : search.categoryIds

  const scopeLoading = filtersByCategory && categoriesQuery.isPending
  const scopeFailed = filtersByCategory && categoriesQuery.isError
  const scopeEmpty = filtersByCategory && !categoriesQuery.isPending && productIds.length === 0

  const scopedStoreIds =
    search.storeIds.length > 0 ? search.storeIds : stores.map((store) => store.storeId)

  const queriesEnabled = enabled && scopedStoreIds.length > 0 && !scopeLoading && !scopeEmpty

  const commonQuery = {
    storeIds: scopedStoreIds,
    startDate: range.start,
    endDate: range.end,
    ...(productIds.length > 0 ? { productIds } : {}),
  }

  const forecastQuery = useQuery(
    api.forecast.shortTermSummary.queryOptions(
      { query: commonQuery },
      { enabled: queriesEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const seriesQuery = useQuery(
    api.forecast.shortTermDailySeries.queryOptions(
      {
        query: {
          ...commonQuery,
          groupBy: SEGMENT_GROUP_BY[search.chartSegment],
        },
      },
      { enabled: queriesEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const storeLabels = new Map(stores.map((store) => [store.storeId, store.label]))

  const forecastRows = forecastQuery.data?.products ?? []
  const storeRows = buildStoreRows(forecastQuery.data?.stores ?? [], storeLabels)
  const zeroShareProducts = forecastQuery.data?.zeroShareProducts

  const totauxState = resolveDashboardCardState(forecastQuery)
  const boutiquesState = resolveDashboardCardState(forecastQuery, storesQuery)
  const categoriesState = resolveDashboardCardState(forecastQuery, categoriesQuery)
  const chartState = resolveDashboardCardState(seriesQuery, categoriesQuery, segmentCategoriesQuery)

  const grid = (children: JSX.Element) => (
    <div
      className="grid min-h-0 flex-1 gap-2"
      style={{ gridTemplateColumns: GRID_COLUMNS, gridTemplateRows: 'auto minmax(0, 1fr)' }}
    >
      {children}
    </div>
  )

  const content = () => {
    if (scopedStoreIds.length === 0 || scopeLoading) {
      return grid(
        <>
          <PrevisionsCardSkeleton variant="totaux" />
          <PrevisionsCardSkeleton variant="chart" />
          <PrevisionsCardSkeleton variant="boutiques" />
          <PrevisionsCardSkeleton variant="categories" />
        </>,
      )
    }
    if (scopeFailed) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-amber-600">
            Erreur lors du chargement des catégories : le filtre de l’URL ne peut pas être appliqué.
          </p>
          <Button variant="outline" size="sm" onClick={() => categoriesQuery.refetch()}>
            Réessayer
          </Button>
        </div>
      )
    }
    if (scopeEmpty) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-muted-foreground">
            Les catégories filtrées ne contiennent aucun produit actif : aucune prévision ne peut
            être calculée sur ce périmètre.
          </p>
          <Button variant="outline" size="sm" onClick={() => setSearch({ categoryIds: [] })}>
            Réinitialiser les catégories
          </Button>
        </div>
      )
    }
    if (forecastQuery.data && forecastRows.length === 0) {
      return <p className="text-muted-foreground">Aucune production prévue sur ce périmètre.</p>
    }

    return grid(
      <>
        <PrevisionsCardBoundary
          state={totauxState}
          variant="totaux"
          tone="totaux"
          errorMessage="Totaux indisponibles"
          onRetry={() => retryFailedDashboardQueries(forecastQuery)}
        >
          <TotauxCard totals={buildTotals(forecastRows)} storeCount={storeRows.length} />
        </PrevisionsCardBoundary>

        <PrevisionsCardBoundary
          state={chartState}
          variant="chart"
          tone="chart"
          errorMessage="Séries indisponibles"
          onRetry={() =>
            retryFailedDashboardQueries(seriesQuery, categoriesQuery, segmentCategoriesQuery)
          }
        >
          <ProductionChart
            series={seriesQuery.data?.series ?? []}
            categories={legendCategoriesFor(search.chartSegment)}
            measure={search.measure}
            segment={search.chartSegment}
            onMeasureChange={(measure) => setSearch({ measure })}
            onSegmentChange={(chartSegment) => setSearch({ chartSegment })}
          />
        </PrevisionsCardBoundary>

        <PrevisionsCardBoundary
          state={boutiquesState}
          variant="boutiques"
          tone="neutral"
          errorMessage="Boutiques indisponibles"
          onRetry={() => retryFailedDashboardQueries(forecastQuery, storesQuery)}
        >
          <BoutiquesCard stores={storeRows} onShowDaily={setDailyScope} />
        </PrevisionsCardBoundary>

        <PrevisionsCardBoundary
          state={categoriesState}
          variant="categories"
          tone="neutral"
          errorMessage="Catégories indisponibles"
          onRetry={() => retryFailedDashboardQueries(forecastQuery, categoriesQuery)}
        >
          <CategoriesTable
            categories={buildCategoryTree(
              forecastRows,
              legendCategoriesFor(search.tableSegment),
              search.tableSegment === 'uf' ? 'commercial' : search.tableSegment,
            )}
            rows={forecastRows}
            storeCount={storeRows.length}
            segment={search.tableSegment}
            zeroShareProducts={zeroShareProducts}
            onSegmentChange={(tableSegment) => setSearch({ tableSegment })}
          />
        </PrevisionsCardBoundary>
      </>,
    )
  }

  if (!enabled) {
    return (
      <div className={PAGE_CLASS}>
        <AppHeader className={HEADER_CLASS}>
          <Title className={TITLE_CLASS}>Prévisions</Title>
        </AppHeader>
        <AppContainer className={CONTAINER_CLASS}>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </div>
    )
  }

  return (
    <div className={PAGE_CLASS}>
      <AppHeader className={HEADER_CLASS}>
        <Title className={TITLE_CLASS}>Prévisions</Title>
      </AppHeader>
      <AppContainer className={CONTAINER_CLASS}>
        <FiltersBar
          storeIds={scopedStoreIds}
          onStoreIdsChange={(storeIds) => setSearch({ storeIds })}
          range={range}
          onRangeChange={({ start, end }) => setSearch({ startDate: start, endDate: end })}
          categoryIds={appliedCategoryIds}
          onCategoryIdsChange={(categoryIds) => setSearch({ categoryIds })}
          categories={categories}
        />
        {content()}

        {dailyScope && (
          <BoutiqueChartModal
            scope={dailyScope}
            range={range}
            productIds={productIds}
            categoriesForSegment={legendCategoriesFor}
            measure={search.measure}
            segment={search.chartSegment}
            onClose={() => setDailyScope(null)}
          />
        )}
      </AppContainer>
    </div>
  )
}
